import React, { useMemo, useRef, useCallback, memo } from 'react';
import { Rnd } from 'react-rnd';
import { motion, useReducedMotion } from 'framer-motion';
import { useMobileViewport } from '../hooks/useMobileViewport';
import { getWindowTheme } from '../config/windowTheme';

interface WindowProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  appId: string;
  isActive: boolean;
  onFocus: () => void;
  isMinimized: boolean;
  onMinimize: () => void;
  icon: string;
  zIndex: number;
  isDarkMode?: boolean;
  openIndex?: number;
}

// Module-level variant objects avoid allocating new objects on every render.
const VARIANTS_FULL = {
  initial:   { opacity: 0,  scale: 0.95, y: 20  },
  open:      { opacity: 1,  scale: 1,    y: 0   },
  minimized: { opacity: 0,  scale: 0.8,  y: 100 },
  exit:      { opacity: 0,  scale: 0.95, y: 20  },
};

// Stripped-down variants that respect the OS "reduce motion" preference.
const VARIANTS_REDUCED = {
  initial:   { opacity: 0 },
  open:      { opacity: 1 },
  minimized: { opacity: 0 },
  exit:      { opacity: 0 },
};

const SPRING_FULL    = { type: 'spring', stiffness: 300, damping: 25, mass: 0.5 } as const;
const SPRING_REDUCED = { duration: 0.15 } as const;

const Window = memo(({
  isOpen,
  onClose,
  title,
  children,
  isActive,
  onFocus,
  appId,
  isMinimized,
  onMinimize,
  icon,
  zIndex,
  isDarkMode = false,
  openIndex = 0,
}: WindowProps): React.ReactElement | null => {
  const prefersReducedMotion = useReducedMotion();
  const variants   = prefersReducedMotion ? VARIANTS_REDUCED : VARIANTS_FULL;
  const transition = prefersReducedMotion ? SPRING_REDUCED   : SPRING_FULL;

  const isMobile = useMobileViewport();

  // Freeze pointer-events over content while dragging to prevent jank on
  // content-heavy windows (e.g. the artworks gallery).
  const isDraggingRef = useRef(false);
  const contentRef    = useRef<HTMLDivElement>(null);

  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true;
    if (contentRef.current) contentRef.current.style.pointerEvents = 'none';
    onFocus();
  }, [onFocus]);

  const handleDragStop = useCallback(() => {
    isDraggingRef.current = false;
    if (contentRef.current) contentRef.current.style.pointerEvents = '';
  }, []);

  const handleResizeStart = useCallback(() => {
    onFocus();
    if (contentRef.current) contentRef.current.style.pointerEvents = 'none';
  }, [onFocus]);

  const handleResizeStop = useCallback(() => {
    if (contentRef.current) contentRef.current.style.pointerEvents = '';
  }, []);

  const theme = getWindowTheme(isDarkMode);

  /* ── initial size / position ── */
  const initialPosition = useMemo(() => {
    const TASKBAR = 68;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (vw < 640) {
      return { x: 0, y: 0, width: vw, height: vh - TASKBAR };
    }

    const w = Math.min(1280, Math.max(920, vw * 0.88));
    const h = Math.min(860,  Math.max(640, (vh - TASKBAR) * 0.92));

    const CASCADE   = 36;
    const MAX_SLOTS = 8;
    const slot      = openIndex % MAX_SLOTS;
    const baseX     = (vw - w) / 2;
    const baseY     = (vh - TASKBAR - h) / 2 - 20;

    return {
      x:      Math.max(0, Math.min(baseX + slot * CASCADE, vw - 500)),
      y:      Math.max(0, Math.min(baseY + slot * CASCADE, vh - TASKBAR - 200)),
      width:  w,
      height: h,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOpen) return null;

  const isImageIcon = typeof icon === 'string' && icon.includes('.');

  return (
    <motion.div
      key={appId}
      variants={variants}
      initial="initial"
      animate={isMinimized ? 'minimized' : 'open'}
      exit="exit"
      transition={transition}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex }}
    >
      <Rnd
        default={initialPosition}
        minWidth={isMobile ? window.innerWidth : 400}
        minHeight={isMobile ? window.innerHeight - 68 : 500}
        maxWidth="100%"
        maxHeight="calc(100% - 80px)"
        bounds="parent"
        dragHandleClassName="window-header"
        disableDragging={isMobile}
        enableResizing={isMobile ? false : undefined}
        className="pointer-events-auto"
        onDragStart={handleDragStart}
        onDragStop={handleDragStop}
        onResizeStart={handleResizeStart}
        onResizeStop={handleResizeStop}
      >
        {/* Pet collision sentinel */}
        <div
          data-window-surface="true"
          data-minimized={isMinimized ? 'true' : 'false'}
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
        />

        {/* Window shell */}
        <div
          className={`w-full h-full flex flex-col ${theme.bg} ${theme.text} rounded-lg shadow-lg overflow-hidden border ${
            isActive ? 'border-blue-500/50' : isDarkMode ? 'border-white/[0.07]' : 'border-black/[0.07]'
          }`}
          style={{ contain: isMobile ? undefined : 'layout style', willChange: 'transform' }}
          onMouseDown={onFocus}
        >
          {/* Title bar */}
          <div
            className={`window-header flex-shrink-0 ${
              isDarkMode
                ? 'bg-gray-800'
                : 'bg-gradient-to-r from-pink-200 to-purple-200'
            } flex justify-between items-center select-none px-3 py-2 sm:px-3 sm:py-2`}
            style={{ minHeight: isMobile ? 52 : 40 }}
          >
            <div className="flex items-center gap-2 min-w-0">
              {isImageIcon ? (
                <img
                  src={icon}
                  alt={title}
                  className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 object-contain"
                />
              ) : (
                <span className="text-lg sm:text-xl flex-shrink-0">{icon}</span>
              )}
              <h3
                className={`window-title truncate ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                {title}
              </h3>
            </div>

            <div className="flex items-center gap-2 sm:gap-2 flex-shrink-0 ml-2">
              {!isMobile && (
                <motion.button
                  onClick={(e) => { e.stopPropagation(); onMinimize(); }}
                  className="p-2 -m-2 flex items-center justify-center"
                  aria-label="Minimize window"
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.85 }}
                >
                  <span className="block w-3.5 h-3.5 rounded-full bg-yellow-400 hover:bg-yellow-500 transition-colors" />
                </motion.button>
              )}

              <motion.button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                onTouchEnd={(e) => { e.stopPropagation(); onClose(); }}
                className={
                  isMobile
                    ? `flex items-center justify-center rounded-full bg-red-500 active:bg-red-600
                       text-white font-bold leading-none w-10 h-10`
                    : 'p-2 -m-2 flex items-center justify-center'
                }
                aria-label="Close window"
                whileTap={{ scale: 0.85 }}
              >
                {isMobile ? (
                  <svg viewBox="0 0 14 14" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
                    <line x1="2" y1="2" x2="12" y2="12" />
                    <line x1="12" y1="2" x2="2" y2="12" />
                  </svg>
                ) : (
                  <span className="block w-3.5 h-3.5 rounded-full bg-red-500 hover:bg-red-600 transition-colors" />
                )}
              </motion.button>
            </div>
          </div>

          {/* Scrollable content — hidden (not unmounted) when minimized to
              preserve scroll position and component state while avoiding
              unnecessary layout/paint work on invisible content. */}
          <div
            ref={contentRef}
            className={`flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch] ${theme.bg}`}
            style={isMinimized ? { visibility: 'hidden' } : undefined}
          >
            {children}
          </div>
        </div>
      </Rnd>
    </motion.div>
  );
});

Window.displayName = 'Window';

export default Window;
